import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Loader2,
  CheckCircle2,
  UserPlus,
  Users,
  Award,
  Building2,
  MapPin,
  Plus,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';

/**
 * Get status badge styles matching the qualifications tab
 * Handles both matrix data status values ('valid', 'soon', 'expired', 'missing')
 * and qualification status values ('completed', 'pending')
 */
function getStatusBadgeStyles(status, expiryDate) {
  const now = new Date();
  const expiry = expiryDate ? new Date(expiryDate) : null;
  const isExpiringSoon = expiry && expiry > now && expiry <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const isExpired = expiry && expiry < now;

  // Handle matrix data status values
  if (status === 'valid') {
    return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Valid' };
  } else if (status === 'soon' || status === 'expiring') {
    return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', label: 'Expiring' };
  } else if (status === 'expired') {
    return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Expired' };
  }

  // Handle qualification status values (from user_qualifications)
  if (status === 'completed' && !isExpired && !isExpiringSoon) {
    return { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Valid' };
  } else if (status === 'completed' && isExpiringSoon) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', label: 'Expiring' };
  } else if (status === 'completed' && isExpired) {
    return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Expired' };
  } else if (status === 'pending') {
    return { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', label: 'Pending' };
  }

  return { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200', label: 'Missing' };
}

/**
 * Check if a qualification is valid or expiring (not expired)
 */
function isValidOrExpiring(status, expiryDate) {
  if (status !== 'completed') return false;
  if (!expiryDate) return true; // No expiry = valid
  const expiry = new Date(expiryDate);
  const now = new Date();
  return expiry >= now; // Valid if not yet expired
}

/**
 * Format date for display - handles both ISO dates and pre-formatted strings
 */
function formatDate(dateValue) {
  if (!dateValue) return '';

  // If it's already a formatted string (like "24/04/26"), return as-is
  if (typeof dateValue === 'string' && dateValue.match(/^\d{2}\/\d{2}\/\d{2}$/)) {
    return dateValue;
  }

  // Try to parse as date
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) {
    // If parsing failed, return the original value
    return dateValue;
  }

  return date.toLocaleDateString();
}

/**
 * Member row component - clean professional design matching RecommendationsPanel
 */
function MemberRow({
  member,
  status,
  expiryDate,
  variant = 'space', // 'space' or 'company'
  onAction,
  actionLabel = 'View',
  isLoading = false,
  showQualificationBadge = true,
  showTrainedIcon = false,
  userId = null
}) {
  const navigate = useNavigate();
  const initials = member.name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  const styles = getStatusBadgeStyles(status, expiryDate);

  // Get the user ID for profile navigation
  const profileUserId = userId || member.userId || member.user_id;

  const handleProfileClick = (e) => {
    e.stopPropagation();
    if (profileUserId) {
      navigate(`/profile/${profileUserId}`);
    }
  };

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-lg border transition-colors",
      "bg-white border-gray-100 hover:border-gray-200"
    )}>
      {/* Avatar - clickable */}
      <Avatar
        className={cn(
          "h-9 w-9 flex-shrink-0",
          profileUserId && "cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all"
        )}
        onClick={profileUserId ? handleProfileClick : undefined}
      >
        <AvatarImage src={member.avatar || member.avatar_url} />
        <AvatarFallback className="text-xs font-medium bg-gray-100 text-gray-700">
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Info */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span
          className={cn(
            "text-sm font-medium text-gray-900 truncate",
            profileUserId && "cursor-pointer hover:text-blue-600 hover:underline transition-colors"
          )}
          onClick={profileUserId ? handleProfileClick : undefined}
        >
          {member.name}
        </span>
        {showTrainedIcon && (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
        )}
        {(member.role || member.jobTitle || member.department) && (
          <>
            <span className="text-xs text-gray-400 flex-shrink-0">•</span>
            <span className="text-xs text-gray-500 truncate">
              {[member.role || member.jobTitle, member.department].filter(Boolean).join(' · ')}
            </span>
          </>
        )}
      </div>

      {/* Status Badge */}
      {showQualificationBadge && (
        <Badge
          variant="outline"
          className={cn(
            "text-[10px] px-1.5 py-0 h-5 flex-shrink-0",
            styles.bg, styles.text, styles.border
          )}
        >
          {styles.label}
        </Badge>
      )}

      {/* Expiry Date - hidden on mobile */}
      {expiryDate && (
        <div className="hidden sm:flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
          <Clock className="h-3 w-3" />
          {formatDate(expiryDate)}
        </div>
      )}

      {/* Action Button */}
      {onAction && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAction(member)}
          disabled={isLoading}
          className="h-8 w-[100px] flex-shrink-0"
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              {actionLabel}
            </>
          )}
        </Button>
      )}
    </div>
  );
}

/**
 * Compact stats bar - clean professional layout
 */
function StatsSummary({ spaceCount, companyCount, isLoading }) {
  const stats = [
    {
      label: 'In Space',
      value: spaceCount,
      icon: MapPin,
    },
    {
      label: 'Available',
      value: isLoading ? '...' : companyCount,
      icon: Building2,
    }
  ];

  return (
    <div className="flex items-center justify-center gap-6 py-3 px-4 bg-gray-50 rounded-lg border border-gray-100">
      {stats.map((stat, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <stat.icon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">{stat.label}</span>
          <span className="text-sm font-semibold text-gray-700">{stat.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * QualificationMembersPanel - 2-column layout showing space members and company members
 * Matching the clean professional styling of RecommendationsPanel
 */
export function QualificationMembersPanel({
  qualification,
  spaceMembers = [],
  companyId,
  projectId,
  stackId = null,
  stackProjects = [],
  isStackView = false,
  currentTeamMemberIds = [],
  onMemberClick,
  onAddToSpace,
  isLoadingCompany = false,
  companyMembersData = null,
  fetchCompanyMembers
}) {
  const [companyMembers, setCompanyMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, member: null, selectedProjectId: null });
  const [addingMemberId, setAddingMemberId] = useState(null);

  // Fetch company members with this qualification when panel is mounted
  useEffect(() => {
    if (hasFetched || !qualification || !companyId) return;

    const loadCompanyMembers = async () => {
      setIsLoading(true);
      try {
        if (fetchCompanyMembers) {
          const members = await fetchCompanyMembers(qualification, currentTeamMemberIds);
          setCompanyMembers(members);
        } else {
          const members = await fetchCompanyMembersWithQualification(
            qualification,
            companyId,
            currentTeamMemberIds,
            stackId
          );
          setCompanyMembers(members);
        }
      } catch (error) {
        console.error('Error fetching company members:', error);
      } finally {
        setIsLoading(false);
        setHasFetched(true);
      }
    };

    loadCompanyMembers();
  }, [qualification?.id, companyId, hasFetched]);

  // Use provided company members data if available
  useEffect(() => {
    if (companyMembersData) {
      setCompanyMembers(companyMembersData);
      setHasFetched(true);
    }
  }, [companyMembersData]);

  const handleAddToSpace = (member) => {
    const defaultProjectId = isStackView && stackProjects.length > 0
      ? stackProjects[0].id
      : projectId;
    setConfirmDialog({ open: true, member, selectedProjectId: defaultProjectId });
  };

  const handleConfirmAdd = async () => {
    const member = confirmDialog.member;
    const targetProjectId = confirmDialog.selectedProjectId || projectId;
    if (!member || !onAddToSpace) return;

    setAddingMemberId(member.userId);
    setConfirmDialog({ open: false, member: null, selectedProjectId: null });

    try {
      await onAddToSpace(member, qualification, targetProjectId);
      setCompanyMembers(prev => prev.filter(m => m.userId !== member.userId));
    } catch (error) {
      console.error('Error adding member to space:', error);
    } finally {
      setAddingMemberId(null);
    }
  };

  // Filter space members to only show those assigned to this qualification
  const assignedSpaceMembers = useMemo(() => {
    return spaceMembers.filter(member => {
      const req = member.requirements?.find(r => r.id === qualification?.id);
      return req && req.status !== 'add';
    });
  }, [spaceMembers, qualification?.id]);

  return (
    <>
      <div className="space-y-4">
        {/* Stats Summary Bar */}
        <StatsSummary
          spaceCount={assignedSpaceMembers.length}
          companyCount={companyMembers.length}
          isLoading={isLoading}
        />

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column - Space Members */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-green-600" />
                <h3 className="text-sm font-medium text-gray-900">Assigned in Space</h3>
                <Badge variant="secondary" className="ml-auto h-5 px-2 text-xs bg-green-50 text-green-700">
                  {assignedSpaceMembers.length}
                </Badge>
              </div>
            </div>
            <ScrollArea className="h-[280px]">
              <div className="p-3 space-y-2">
                {assignedSpaceMembers.length > 0 ? (
                  assignedSpaceMembers.map((member) => {
                    const req = member.requirements?.find(r => r.id === qualification?.id);
                    // Matrix data uses 'valid', 'soon' for trained members
                    const isTrained = req?.status === 'valid' || req?.status === 'soon' || req?.status === 'completed';
                    return (
                      <MemberRow
                        key={member.id}
                        member={member}
                        userId={member.user_id}
                        status={req?.status}
                        expiryDate={req?.expiry_date || req?.date}
                        variant="space"
                        showTrainedIcon={isTrained}
                      />
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900">No members assigned</p>
                    <p className="text-xs text-gray-500 mt-1">Assign members from the matrix view</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right Column - Company Members */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium text-gray-900">Available from Company</h3>
                <Badge variant="secondary" className="ml-auto h-5 px-2 text-xs">
                  {isLoading ? '...' : companyMembers.length}
                </Badge>
              </div>
            </div>
            <ScrollArea className="h-[280px]">
              <div className="p-3 space-y-2">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    <p className="text-sm text-gray-500">Loading...</p>
                  </div>
                ) : companyMembers.length > 0 ? (
                  companyMembers.map((member) => (
                    <MemberRow
                      key={member.userId}
                      member={member}
                      userId={member.userId}
                      status={member.status}
                      expiryDate={member.expiryDate}
                      variant="company"
                      onAction={handleAddToSpace}
                      actionLabel="Add"
                      isLoading={addingMemberId === member.userId}
                      showTrainedIcon={true}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900">No available members</p>
                    <p className="text-xs text-gray-500 mt-1">No other company members have this qualification</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.open}
        onOpenChange={(open) => !open && setConfirmDialog({ open: false, member: null, selectedProjectId: null })}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader className="px-6 pt-6 pb-4">
            <AlertDialogTitle className="text-lg font-semibold">Add to Space & Assign</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="px-6 space-y-4">
            {/* Member info card */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-100">
              <Avatar className="h-11 w-11">
                <AvatarImage src={confirmDialog.member?.avatar} />
                <AvatarFallback className="text-sm font-medium bg-gray-200 text-gray-700">
                  {confirmDialog.member?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium text-gray-900">{confirmDialog.member?.name}</p>
                <p className="text-sm text-gray-500">
                  {confirmDialog.member?.role || confirmDialog.member?.jobTitle || 'Team Member'}
                </p>
              </div>
            </div>

            {/* Space selector for stack view */}
            {isStackView && stackProjects.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="space-select" className="text-sm font-medium text-gray-700">
                  Select space to add to
                </Label>
                <Select
                  value={confirmDialog.selectedProjectId}
                  onValueChange={(value) => setConfirmDialog(prev => ({ ...prev, selectedProjectId: value }))}
                >
                  <SelectTrigger id="space-select" className="w-full">
                    <SelectValue placeholder="Select a space..." />
                  </SelectTrigger>
                  <SelectContent>
                    {stackProjects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description text */}
            <div className="space-y-2 text-sm text-gray-600">
              <p>
                <strong className="text-gray-900">{confirmDialog.member?.name}</strong> is not currently a member of this space.
              </p>
              <p>
                This will add them as a team member and assign <strong className="text-gray-900">{qualification?.name}</strong>.
              </p>
            </div>
          </div>

          <AlertDialogFooter className="px-6 py-4 mt-2 border-t border-gray-100 bg-gray-50/50">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAdd}
              disabled={isStackView && stackProjects.length > 1 && !confirmDialog.selectedProjectId}
            >
              Add & Assign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Fetch company members who have a valid/expiring qualification
 * but are not in the current space/stack team
 */
export async function fetchCompanyMembersWithQualification(
  qualification,
  companyId,
  excludeUserIds = [],
  stackId = null
) {
  if (!qualification || !companyId) return [];

  try {
    // First, get all company members
    const { data: companyMembers, error: companyError } = await supabase
      .from('company_members')
      .select(`
        id,
        user_id,
        role,
        user_profiles!company_members_user_id_fkey (
          id,
          first_name,
          last_name,
          email,
          job_title,
          department,
          avatar_url
        )
      `)
      .eq('company_id', companyId);

    if (companyError) throw companyError;

    // Get user IDs to exclude (current team members)
    const excludeSet = new Set(excludeUserIds);

    // Filter to company members not already in the space
    const eligibleMembers = (companyMembers || []).filter(m => !excludeSet.has(m.user_id));

    if (eligibleMembers.length === 0) return [];

    // Now query user_qualifications for these users
    const eligibleUserIds = eligibleMembers.map(m => m.user_id);

    let qualificationsQuery;

    // Query by course_link if available, otherwise by requirement_id
    if (qualification.course_link_id && qualification.course_link_type) {
      qualificationsQuery = supabase
        .from('user_qualifications')
        .select('user_id, status, expiry_date')
        .eq('course_link_id', qualification.course_link_id)
        .eq('course_link_type', qualification.course_link_type)
        .eq('status', 'completed')
        .in('user_id', eligibleUserIds);
    } else {
      qualificationsQuery = supabase
        .from('user_qualifications')
        .select('user_id, status, expiry_date')
        .eq('requirement_id', qualification.id)
        .eq('status', 'completed')
        .in('user_id', eligibleUserIds);
    }

    const { data: qualifications, error: qualError } = await qualificationsQuery;

    if (qualError) throw qualError;

    // Filter to only valid/expiring (not expired)
    const validQuals = (qualifications || []).filter(q =>
      isValidOrExpiring(q.status, q.expiry_date)
    );

    // Map qualifications to user data
    const qualMap = new Map();
    validQuals.forEach(q => {
      qualMap.set(q.user_id, { status: q.status, expiryDate: q.expiry_date });
    });

    // Build final list
    const result = eligibleMembers
      .filter(m => qualMap.has(m.user_id))
      .map(m => {
        const qualData = qualMap.get(m.user_id);
        const profile = m.user_profiles;
        return {
          userId: m.user_id,
          companyMemberId: m.id,
          name: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || 'Unknown',
          email: profile?.email,
          role: m.role,
          jobTitle: profile?.job_title,
          department: profile?.department,
          avatar: profile?.avatar_url,
          status: qualData.status,
          expiryDate: qualData.expiryDate
        };
      });

    return result;
  } catch (error) {
    console.error('Error fetching company members with qualification:', error);
    return [];
  }
}

export default QualificationMembersPanel;
